import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BreadcrumbService, BreadcrumbUrls} from '../../../providers/services/breadcrumb.service';
import {PathNames, Paths, StaticPage, StaticPageContent} from '../../../../config/config';
import {map} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {CatalogService} from '../../../providers/services/catalog.service';
import {QuotationComponent} from '../../../components/modals/quotation/quotation.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {UtilsService} from '../../../providers/services/utils.service';
import {TranslateService} from '@ngx-translate/core';
import {VideoImageGalleryComponent} from '../../../widgets/video-image-gallery/video-image-gallery.component';
import {PagesService} from '../../../providers/services/pages.service';

enum Buttons {
  CATALOG,
  KIT_ACCESSORIES,
  PHOTO,
  VIDEO,
  QUOTATION
}

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit, OnDestroy {

  @ViewChild('videoGallery', {static: false}) videoGallery: VideoImageGalleryComponent;
  @ViewChild('imageGallery', {static: false}) imageGallery: VideoImageGalleryComponent;

  private readonly subscriptions: Subscription = new Subscription();
  public product;

  public buttons;
  public schema;
  public staticData;
  public staticPageContent = StaticPageContent[StaticPage.Product];

  constructor(
    private breadcrumbService: BreadcrumbService, private activatedRoute: ActivatedRoute, private catalogService: CatalogService,
    private modalService: NgbModal, private utilsService: UtilsService, private translateService: TranslateService,
    private pagesService: PagesService
  ) {
    this.subscriptions.add(
      this.activatedRoute.data.pipe(map(({product}) => product)).subscribe(({body}) => {
        this.product = body.data.product;
        breadcrumbService.next(
          BreadcrumbUrls.instance
            .addUrl({link: Paths.Products, label: PathNames[Paths.Products]})
            .addUrl({label: body.data.product.name, link: Paths.Product, args: [body.data.product.name_slug]})
        );
        this.loadButtons(this.product);
        this.initSchema(this.product);
      })
    );

    this.subscriptions.add(
      this.pagesService.getPage(StaticPage.Product).subscribe(staticData => this.staticData = staticData.body.data.contents)
    );

  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.breadcrumbService.reset();
  }

  private btnAction(type: Buttons) {
    return () => {
      switch (type) {
        case Buttons.QUOTATION:
          const instance = this.modalService.open(QuotationComponent, {
            backdropClass: 'modals-backdrop-custom',
            windowClass: 'quotation-window'
          });
          instance.componentInstance.product = this.product.name_slug;
          break;
        case Buttons.PHOTO:
          this.imageGallery.openPreview();
          break;
        case Buttons.VIDEO:
          this.videoGallery.openPreview();
          break;
        case Buttons.CATALOG:
          this.utilsService.openLink(this.product.attachment_catalog.path);
          break;
        case Buttons.KIT_ACCESSORIES:
          this.utilsService.openLink(this.product.attachment_kit_accessories.path);
          break;
      }
    };
  }

  private loadButtons(product) {
    this.buttons = [];
    if (product.attachment_catalog) {
      this.buttons.push({
        label: this.translateService.instant('label_download_catalog'),
        action: this.btnAction(Buttons.CATALOG).bind(this),
        outline: true
      });
    }
    if (product.attachment_kit_accessories) {
      this.buttons.push({
        label: this.translateService.instant('label_download_kit_and_accessories'),
        action: this.btnAction(Buttons.KIT_ACCESSORIES).bind(this),
        outline: true
      });
    }
    if (product.images_secondary_gallery && product.images_secondary_gallery.length) {
      this.buttons.push({
        label: this.translateService.instant('label_photo_gallery'),
        action: this.btnAction(Buttons.PHOTO).bind(this),
        outline: true
      });
    }
    if (product.videos_gallery && product.videos_gallery.length) {
      this.buttons.push({
        label: this.translateService.instant('label_video_gallery'),
        action: this.btnAction(Buttons.VIDEO).bind(this),
        outline: true
      });
    }
    this.buttons.push({
      label: this.translateService.instant('label_quotation'),
      action: this.btnAction(Buttons.QUOTATION).bind(this)
    });
  }

  private initSchema(product) {
    this.schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      sku: product.sku,
      name: product.name,
      description: product.description,
      image: product.image_header.path,
      itemCondition: 'NEW',
      offers: {
        '@type': 'Offer',
        price: '0'
      }
    };
  }
}
